type Options = {
  /**
   * @default 50
   */
  percentage?: number

  /**
   * @default '.js-dragger'
   */
  identifier?: string
}

type Callbacks = {
  slide?(percent: number): void
  mouseDown?(): void
  mouseUp?(): void
}

class Slider {
  private dragger!: HTMLElement
  private track!: HTMLElement
  private container!: HTMLElement
  private dragging: boolean

  private percentage!: number

  constructor(
    private callbacks: Callbacks,
    options: Options,
  ) {
    this.dragger = document.querySelector(options.identifier || '.js-dragger')!
    this.track = this.dragger.parentNode! as HTMLElement
    this.container = this.track.parentNode! as HTMLElement
    this.dragging = false

    this.percentage = options.percentage || 50
    this.bindControls()
  }

  #handleMouseMove(event: MouseEvent) {
    event.preventDefault()

    if (!this.dragging) {
      return
    }

    const trackBbox = this.track.getBoundingClientRect()
    const draggerBbox = this.dragger.getBoundingClientRect()
    const ownerWindow = this.track.ownerDocument.defaultView!
    const min = 0
    const max = trackBbox.width - draggerBbox.width

    let left = event.clientX! - (trackBbox.left + ownerWindow.scrollX)

    if (left < min) {
      left = min
    }
    if (left > max) {
      left = max
    }
    this.dragger.style.left = `${left}px`

    this.percentage = (left / max) * 100
    window.debug(`Slider at ${this.percentage}%`)

    this.callbacks.slide?.(this.percentage)
  }

  #handleMouseUp() {
    if (!this.dragging) {
      return
    }

    document.removeEventListener('mousemove', this.#handleMouseMove)

    this.dragging = false
    document.body.style.cursor = 'auto'

    return this.callbacks.mouseUp?.()
  }

  public bindControls = () => {
    this.dragger.addEventListener('mousedown', mousedownEvent => {
      mousedownEvent.preventDefault()

      this.dragging = true
      document.body.style.cursor = 'pointer'
      this.callbacks.mouseDown?.()

      document.addEventListener('mousemove', this.#handleMouseMove.bind(this))
      document.addEventListener('mouseup', this.#handleMouseUp.bind(this), {once: true})
    })
  }

  public value = () => {
    return this.percentage
  }

  public setDragger = () => {
    const trackBbox = this.track.getBoundingClientRect()
    const draggerBbox = this.dragger.getBoundingClientRect()
    const max = trackBbox.width - draggerBbox.width
    const left = (this.percentage / 100) * max

    this.dragger.style.left = `${left}px`
  }

  public show = () => {
    this.container.style.display = 'block'
    this.setDragger()
  }

  public hide = () => {
    this.container.style.display = 'none'
  }
}

export default Slider
